package org.teddapp.project.ui.objectives

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Category
import kotlinx.coroutines.launch
import org.teddapp.project.db.repo.CategoryRepository
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTokens

/**
 * Modal de gestion des catégories. Liste éditables + form add new (name + couleur + icône presets).
 */

private val CategoryColorPalette = listOf(
    "#6366F1", "#22D3EE", "#10B981", "#F59E0B",
    "#EF4444", "#EC4899", "#8B5CF6", "#0EA5E9",
)

private val CategoryIconPalette = listOf(
    "target", "heart", "flame", "spark",
    "book", "wallet", "users", "note",
)

@Composable
fun CategoryManagerDialog(
    categories: List<Category>,
    repository: CategoryRepository,
    onClose: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var newName by remember { mutableStateOf("") }
    var newColor by remember { mutableStateOf(CategoryColorPalette[0]) }
    var newIcon by remember { mutableStateOf(CategoryIconPalette[0]) }
    var editingId by remember { mutableStateOf<String?>(null) }
    var editingName by remember { mutableStateOf("") }
    var editingColor by remember { mutableStateOf("") }
    var editingIcon by remember { mutableStateOf("") }
    var pendingDelete by remember { mutableStateOf<String?>(null) }

    val nameError = newName.isNotEmpty() && newName.isBlank()
    val canAdd = newName.trim().isNotEmpty() &&
        categories.none { it.name.equals(newName.trim(), ignoreCase = true) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.42f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClose,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .widthIn(min = 440.dp, max = 520.dp)
                .heightForDialog()
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {},
                ),
        ) {
            // ── Header ──
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    .padding(horizontal = 22.dp, vertical = 16.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "CATÉGORIES",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.SemiBold,
                        letterSpacing = 0.6.sp,
                    )
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = "Gérer mes catégories",
                        color = TeddTokens.Ink,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
                TeddIconButton(onClick = onClose, size = 30.dp) {
                    Box(modifier = Modifier.rotate(45f)) {
                        TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted)
                    }
                }
            }

            // ── Liste éditable ──
            Column(
                modifier = Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 22.dp, vertical = 14.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                if (categories.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .padding(16.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = "Aucune catégorie. Ajoutes-en une ci-dessous.",
                            color = TeddTokens.InkMuted,
                            fontSize = 12.sp,
                        )
                    }
                } else {
                    categories.forEach { cat ->
                        val isEditing = editingId == cat.id
                        if (isEditing) {
                            CategoryEditRow(
                                name = editingName,
                                color = editingColor,
                                icon = editingIcon,
                                onNameChange = { editingName = it },
                                onColorChange = { editingColor = it },
                                onIconChange = { editingIcon = it },
                                onSave = {
                                    val trimmed = editingName.trim()
                                    if (trimmed.isNotEmpty()) {
                                        scope.launch {
                                            repository.update(
                                                id = cat.id,
                                                name = trimmed,
                                                color = editingColor,
                                                icon = editingIcon,
                                                orderIndex = cat.order_index,
                                            )
                                        }
                                    }
                                    editingId = null
                                },
                                onCancel = { editingId = null },
                            )
                        } else {
                            CategoryDisplayRow(
                                category = cat,
                                onEdit = {
                                    editingId = cat.id
                                    editingName = cat.name
                                    editingColor = cat.color
                                    editingIcon = cat.icon
                                },
                                onDelete = { pendingDelete = cat.id },
                            )
                        }
                    }
                }
            }

            // ── Form add new ──
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    .background(TeddTokens.SurfaceSunken)
                    .padding(22.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Text(
                    text = "AJOUTER UNE CATÉGORIE",
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 0.6.sp,
                )
                CategoryFormFields(
                    name = newName,
                    color = newColor,
                    icon = newIcon,
                    onNameChange = { newName = it },
                    onColorChange = { newColor = it },
                    onIconChange = { newIcon = it },
                )
                if (newName.trim().isNotEmpty() && categories.any { it.name.equals(newName.trim(), ignoreCase = true) }) {
                    Text(
                        text = "Une catégorie avec ce nom existe déjà.",
                        color = TeddTokens.Danger,
                        fontSize = 11.sp,
                    )
                }
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TeddButton(
                        text = "Ajouter",
                        onClick = {
                            scope.launch {
                                repository.create(
                                    name = newName.trim(),
                                    color = newColor,
                                    icon = newIcon,
                                    orderIndex = categories.size.toLong(),
                                )
                                newName = ""
                                newColor = CategoryColorPalette[0]
                                newIcon = CategoryIconPalette[0]
                            }
                        },
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Md,
                        enabled = canAdd,
                        leadingIcon = {
                            TeddIcon(name = "plus", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp)
                        },
                    )
                }
            }
        }
    }

    // Confirm delete dialog
    if (pendingDelete != null) {
        val cat = categories.firstOrNull { it.id == pendingDelete }
        if (cat != null) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.42f))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = { pendingDelete = null },
                    ),
                contentAlignment = Alignment.Center,
            ) {
                Column(
                    modifier = Modifier
                        .width(360.dp)
                        .clip(RoundedCornerShape(TeddTokens.RMd))
                        .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = {},
                        )
                        .padding(20.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    Text(
                        text = "Supprimer « ${cat.name} » ?",
                        color = TeddTokens.Ink,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Text(
                        text = "Les objectifs liés ne seront pas supprimés mais perdent leur catégorie.",
                        color = TeddTokens.InkMuted,
                        fontSize = 12.sp,
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
                    ) {
                        TeddButton(
                            text = "Annuler",
                            onClick = { pendingDelete = null },
                            kind = TeddButtonKind.Secondary,
                            size = TeddButtonSize.Md,
                        )
                        TeddButton(
                            text = "Supprimer",
                            onClick = {
                                scope.launch { repository.softDelete(cat.id) }
                                pendingDelete = null
                            },
                            kind = TeddButtonKind.Danger,
                            size = TeddButtonSize.Md,
                        )
                    }
                }
            }
        } else {
            pendingDelete = null
        }
    }
}

@Composable
private fun CategoryDisplayRow(
    category: Category,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
) {
    val color = runCatching { Color(parseHexColor(category.color)) }.getOrElse { TeddTokens.LimeStrong }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(28.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(color.copy(alpha = 0.15f), RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = category.icon, size = 14.dp, color = color, strokeWidth = 2.dp)
        }
        Text(
            text = category.name,
            color = TeddTokens.Ink,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.weight(1f),
        )
        TeddIconButton(onClick = onEdit, size = 28.dp) {
            TeddIcon(name = "edit", size = 13.dp, color = TeddTokens.InkMuted)
        }
        TeddIconButton(onClick = onDelete, size = 28.dp) {
            TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted)
        }
    }
}

@Composable
private fun CategoryEditRow(
    name: String,
    color: String,
    icon: String,
    onNameChange: (String) -> Unit,
    onColorChange: (String) -> Unit,
    onIconChange: (String) -> Unit,
    onSave: () -> Unit,
    onCancel: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.LimeStrong.copy(alpha = 0.04f), RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.LimeStrong.copy(alpha = 0.35f), RoundedCornerShape(TeddTokens.RSm))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        CategoryFormFields(
            name = name,
            color = color,
            icon = icon,
            onNameChange = onNameChange,
            onColorChange = onColorChange,
            onIconChange = onIconChange,
        )
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End)) {
            TeddButton(
                text = "Annuler",
                onClick = onCancel,
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
            )
            TeddButton(
                text = "Enregistrer",
                onClick = onSave,
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Sm,
                enabled = name.trim().isNotEmpty(),
            )
        }
    }
}

@Composable
private fun CategoryFormFields(
    name: String,
    color: String,
    icon: String,
    onNameChange: (String) -> Unit,
    onColorChange: (String) -> Unit,
    onIconChange: (String) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(36.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 12.dp),
            contentAlignment = Alignment.CenterStart,
        ) {
            BasicTextField(
                value = name,
                onValueChange = onNameChange,
                singleLine = true,
                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                decorationBox = { inner ->
                    if (name.isEmpty()) {
                        Text("Nom de la catégorie", color = TeddTokens.InkSubtle, fontSize = 13.sp)
                    }
                    inner()
                },
            )
        }
        Column {
            Text(
                text = "Couleur",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.padding(bottom = 6.dp),
            )
            androidx.compose.foundation.layout.FlowRow(
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                CategoryColorPalette.forEach { hex ->
                    val c = runCatching { Color(parseHexColor(hex)) }.getOrElse { TeddTokens.LimeStrong }
                    val selected = color.equals(hex, ignoreCase = true)
                    Box(
                        modifier = Modifier
                            .size(if (selected) 32.dp else 28.dp)
                            .clip(CircleShape)
                            .background(c, CircleShape)
                            .then(
                                if (selected) Modifier.border(2.dp, TeddTokens.Ink, CircleShape)
                                else Modifier
                            )
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { onColorChange(hex) },
                            )
                    )
                }
            }
        }
        Column {
            Text(
                text = "Icône",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.padding(bottom = 6.dp),
            )
            androidx.compose.foundation.layout.FlowRow(
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                CategoryIconPalette.forEach { iconName ->
                    val selected = icon == iconName
                    val c = runCatching { Color(parseHexColor(color)) }.getOrElse { TeddTokens.LimeStrong }
                    Box(
                        modifier = Modifier
                            .size(34.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(
                                if (selected) c.copy(alpha = 0.18f) else TeddTokens.SurfaceSunken,
                                RoundedCornerShape(TeddTokens.RSm),
                            )
                            .border(
                                1.dp,
                                if (selected) c else TeddTokens.Line,
                                RoundedCornerShape(TeddTokens.RSm),
                            )
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { onIconChange(iconName) },
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        TeddIcon(
                            name = iconName,
                            size = 16.dp,
                            color = if (selected) c else TeddTokens.InkMuted,
                            strokeWidth = 1.8.dp,
                        )
                    }
                }
            }
        }
    }
}

private fun Modifier.heightForDialog(): Modifier = this.then(Modifier.height(620.dp))

private fun parseHexColor(hex: String): Long {
    val clean = hex.trimStart('#')
    val rgb = clean.toLong(16)
    return 0xFF000000L or rgb
}
